import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Lock, ArrowLeft, Mail, LifeBuoy, HelpCircle } from 'lucide-react';

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-vault" data-testid="support-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-[#9333EA] flex items-center justify-center">
                <Lock className="h-4 w-4 text-white" />
              </div>
              <span className="font-heading text-lg font-bold text-white tracking-tight">FutureVault</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-[#A19DB0] hover:text-white hover:bg-white/5" data-testid="support-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="h-20 w-20 rounded-2xl bg-[#9333EA] flex items-center justify-center mx-auto mb-6">
            <LifeBuoy className="h-10 w-10 text-white" />
          </div>
          <h1 className="font-heading text-4xl font-black text-white tracking-tight mb-3">Support</h1>
          <p className="text-[#A19DB0] text-lg">We&apos;re here to help you lock in your savings.</p>
        </div>

        {/* Contact card */}
        <div className="p-6 bg-gradient-to-br from-[#9333EA]/10 to-transparent rounded-xl border border-[#9333EA]/20 mb-10">
          <div className="flex items-center gap-3 mb-3">
            <Mail className="h-5 w-5 text-[#9333EA]" />
            <h2 className="font-heading text-xl font-bold text-white">Contact us</h2>
          </div>
          <p className="text-[#A19DB0] mb-4">
            For account issues, deposits, withdrawals, refunds, or anything else — email us and we&apos;ll respond
            within 1–2 business days.
          </p>
          <a
            href="mailto:arynnlittlejohn@icloud.com"
            className="inline-flex items-center gap-2 text-white bg-[#9333EA] hover:bg-[#7e22ce] transition-colors px-4 py-2 rounded-lg font-semibold"
            data-testid="support-email-btn"
          >
            <Mail className="h-4 w-4" />
            arynnlittlejohn@icloud.com
          </a>
        </div>

        {/* FAQ */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-6">
            <HelpCircle className="h-5 w-5 text-[#9333EA]" />
            <h2 className="font-heading text-2xl font-bold text-white">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            <FAQ
              q="How do I deposit money into a vault?"
              a="Open a vault, tap Deposit, choose an amount, and you'll be taken to a secure Stripe Checkout page. Once payment completes, your funds are held in your linked Stripe Connect account until your unlock date."
            />
            <FAQ
              q="Can I withdraw early?"
              a="No. That's the point of FutureVault — your money stays locked until the unlock date you chose. Commitment vaults and No Escape vaults add extra friction/penalties on top."
            />
            <FAQ
              q="How do I get my money out on the unlock date?"
              a="On or after the unlock date, open the vault and tap Withdraw. The funds are sent via Stripe payout to the bank account you connected. Standard Stripe payout timing (typically 1–2 business days) applies."
            />
            <FAQ
              q="What fees do you charge?"
              a="A 2% platform fee on each deposit, plus Stripe's standard processing fees. Premium is $4.99/month for unlimited goals, commitment vaults, and No Escape vaults."
            />
            <FAQ
              q="How do I cancel my Premium subscription?"
              a="On the web, open your dashboard and use the subscription section. On iOS, manage it in Settings → your Apple ID → Subscriptions. On Android, manage it in Google Play → Subscriptions."
            />
            <FAQ
              q="I forgot my password. What do I do?"
              a={<>Go to the <Link to="/forgot-password" className="text-[#9333EA] underline">Forgot Password</Link> page, enter your email, and we&apos;ll send you a reset link.</>}
            />
            <FAQ
              q="Is my money safe?"
              a="Deposits are processed by Stripe and held in your own connected Stripe account — not ours. FutureVault never stores your bank or card numbers. All data is encrypted in transit and at rest."
            />
            <FAQ
              q="How do I close my account?"
              a="Email arynnlittlejohn@icloud.com from your account email and we'll help you close it. Any unlocked funds will be paid out to your linked bank first."
            />
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 text-sm text-[#A19DB0]" data-testid="support-entity-block">
          <address className="not-italic leading-relaxed">
            <span className="font-semibold text-[#D7CCE6]">FutureVault LLC</span><br />
            212 Skyline Ridge Loop<br />
            Sacramento, CA 95834<br />
            Email: <a href="mailto:arynnlittlejohn@icloud.com" className="text-[#9333EA] underline">arynnlittlejohn@icloud.com</a>
          </address>
        </div>

        <div className="mt-6 flex flex-wrap gap-4 text-sm text-[#A19DB0]">
          <Link to="/privacy" className="hover:text-white" data-testid="support-to-privacy-link">Privacy Policy</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-white" data-testid="support-to-terms-link">Terms of Service</Link>
          <span>·</span>
          <Link to="/refunds" className="hover:text-white" data-testid="support-to-refunds-link">Refund Policy</Link>
          <span>·</span>
          <Link to="/about" className="hover:text-white" data-testid="support-to-about-link">About</Link>
        </div>
      </main>
    </div>
  );
}

function FAQ({ q, a }) {
  return (
    <details className="group p-5 bg-[#0A0A0F] rounded-xl border border-white/10 hover:border-white/20 transition-colors">
      <summary className="cursor-pointer text-white font-semibold list-none flex items-start justify-between gap-4">
        <span>{q}</span>
        <span className="text-[#9333EA] text-xl leading-none select-none group-open:rotate-45 transition-transform">+</span>
      </summary>
      <div className="mt-3 text-[#A19DB0] leading-relaxed text-sm">{a}</div>
    </details>
  );
}
